import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Mail, ArrowLeft, CheckCircle } from 'lucide-react';
import { authService } from '../../services/authService';

const ForgotPassword = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const response = await authService.forgotPassword(email);
      if (response?.data?.success) {
        setSuccess(true);
      } else {
        setError(response?.data?.message || 'Failed to send reset email. Please try again.');
      }
    } catch (error) {
      setError(error.response?.data?.message || error.response?.data?.errors?.email?.[0] || 'Failed to send reset email. Please try again.');
    }
    
    setLoading(false);
  };

  if (success) {
    return (
      <div 
        className="min-h-screen flex flex-col justify-end sm:justify-center py-0 sm:py-12 sm:px-6 lg:px-8 relative"
        style={{
          backgroundImage: 'url(/images/01_-_HERO_1.webp)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat'
        }}
      >
        <div className="absolute inset-0 bg-black/50"></div>
        
        <div className="hidden sm:block sm:mx-auto sm:w-full sm:max-w-md relative z-10 mb-8">
          <Link to="/" className="flex justify-center items-center mb-6">
            <img 
              src="/images/VendaLOGO1.png" 
              alt="Venda Logo" 
              className="h-16 w-auto"
            />
          </Link>
        </div>

        <div className="w-full sm:mx-auto sm:w-full sm:max-w-md relative z-10 animate-slideUp">
          <div className="bg-white py-8 px-6 shadow-2xl rounded-t-3xl sm:rounded-2xl sm:px-10 border-t border-gray-100 sm:border">
            {/* Mobile Logo */}
            <div className="sm:hidden flex justify-center mb-6">
              <Link to="/" className="flex justify-center items-center">
                <img 
                  src="/images/VendaLOGO1.png" 
                  alt="Venda Logo" 
                  className="h-12 w-auto"
                />
              </Link>
            </div>

            <div className="text-center">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100 mb-4">
                <CheckCircle className="h-6 w-6 text-green-600" />
              </div>
              
              <h2 className="text-2xl font-medium text-gray-900 mb-2">
                Check your email
              </h2>
              
              <p className="text-gray-600 mb-6">
                We've sent a password reset link to <strong>{email}</strong>
              </p>
              
              <div className="space-y-4">
                <p className="text-sm text-gray-500">
                  Didn't receive the email? Check your spam folder or{' '}
                  <button
                    onClick={() => {
                      setSuccess(false);
                      setEmail('');
                    }}
                    className="text-black hover:text-gray-600 font-light underline"
                  >
                    try again
                  </button>
                </p>
                
                <div className="pt-4">
                  <Link
                    to="/login"
                    className="flex items-center justify-center text-black hover:text-gray-600 font-light underline"
                  >
                    <ArrowLeft className="w-4 h-4 mr-2" />
                    Back to sign in
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div 
      className="min-h-screen flex flex-col justify-end sm:justify-center py-0 sm:py-12 sm:px-6 lg:px-8 relative"
      style={{
        backgroundImage: 'url(/images/01_-_HERO_1.webp)',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat'
      }}
    >
      <div className="absolute inset-0 bg-black/50"></div>
      
      <div className="hidden sm:block sm:mx-auto sm:w-full sm:max-w-md relative z-10 mb-8">
        <Link to="/" className="flex justify-center items-center mb-6">
          <img 
            src="/images/VendaLOGO1.png" 
            alt="Venda Logo" 
            className="h-16 w-auto"
          />
        </Link>
        <h2 className="text-center text-3xl font-medium text-white">
          Forgot your password?
        </h2>
        <p className="mt-2 text-center text-sm text-white">
          No worries, we'll send you reset instructions.
        </p>
      </div>

      <div className="w-full sm:mx-auto sm:w-full sm:max-w-md relative z-10 animate-slideUp max-h-[90vh] overflow-y-auto">
        <div className="bg-white py-8 px-6 shadow-2xl rounded-t-3xl sm:rounded-2xl sm:px-10 border-t border-gray-100 sm:border">
          {/* Mobile Header */}
          <div className="sm:hidden mb-6 text-center">
            <Link to="/" className="flex justify-center items-center mb-4">
              <img 
                src="/images/VendaLOGO1.png" 
                alt="Venda Logo" 
                className="h-12 w-auto"
              />
            </Link>
            <h2 className="text-2xl font-medium text-black mb-2">
              Forgot your password?
            </h2>
            <p className="text-sm text-gray-600">
              No worries, we'll send you reset instructions.
            </p>
          </div>

          <form className="space-y-6" onSubmit={handleSubmit}>
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-md text-sm">
                {error}
              </div>
            )}

            <div>
              <label htmlFor="email" className="block text-sm font-light text-black mb-1">
                Email address
              </label>
              <div className="mt-1 relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Mail className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="input pl-10"
                  placeholder="Enter your email address"
                />
              </div>
              <p className="mt-2 text-sm text-gray-500">
                Enter the email address associated with your account and we'll send you a link to reset your password.
              </p>
            </div>

            <div>
              <button
                type="submit"
                disabled={loading}
                className="w-full flex justify-center items-center py-3 bg-black text-white font-light rounded-md hover:bg-gray-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                ) : (
                  'Send reset link'
                )}
              </button>
            </div>
          </form>

          <div className="mt-6">
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-300" />
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-2 bg-white text-gray-500">Remember your password?</span>
              </div>
            </div>

            <div className="mt-6 text-center">
              <Link
                to="/login"
                className="flex items-center justify-center text-black hover:text-gray-600 font-light underline"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to sign in
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;
